import { BASE_URL } from "../constants";
import { getAuthHeader } from "../utils";

/**
 * Fetches paginated billing data from the API.
 * @param {object} params - The parameters for fetching billing data.
 * @param {number} params.clientID - The ID of the client to fetch billing data for.
 * @param {number} params.page - The page number to fetch.
 * @param {number} params.rows - The number of rows per page.
 * @param {string?} params.period - The billing period to filter by (optional).
 * @returns {Promise<{data: object[], totalRecords: number}>} - The billing data and total record count.
 */
export async function getBillingList({
  clientID,
  page = 1,
  rows = 10,
  period = null,
  medicalCenter = null,
}) {
  try {
    const params = new URLSearchParams();

    params.append("company_client", clientID);

    if (period) {
      params.append("period", period);
    }
    if (medicalCenter) {
      params.append("medical_center", medicalCenter);
    }
    const response = await fetch(
      `${BASE_URL}insurance/billing/?${params.toString()}`,
      {
        headers: {
          ...getAuthHeader(),
        },
        params: {
          page,
          rows,
        },
      }
    );
    if (!response.ok) {
      throw new Error(JSON.stringify(response));
    }
    const data = await response.json();
    return {
      data: data.results,
      totalRecords: data.totalRecords,
    };
  } catch (error) {
    console.error("Error fetching billing list:", error.message);
    return { data: [], totalRecords: 0 };
  }
}

/**
 * Fetches the amount in Bs for a specific billing ID.
 * @param {number} billingID - The ID of the billing to fetch the amount for.
 * @returns {Promise<number|null>} - The amount in Bs, or null if an error occurs.
 */
export async function getBillingAmountBs(billingID) {
  try {
    const response = await fetch(
      `${BASE_URL}insurance/billing/${billingID}/amount_bs/`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );
    if (!response.ok) {
      throw new Error(
        `Error fetching billing amount in Bs: ${response.statusText}`
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching billing amount in Bs:", error.message);
    return null;
  }
}

/**
 * Fetches the details of a specific billing by ID.
 * @param {number} billingId - The ID of the billing to fetch details for.
 * @returns {Promise<object|null>} - The billing details, or null if an error occurs.
 */
export async function getBillingDetail(billingId) {
  try {
    const response = await fetch(`${BASE_URL}insurance/billing/${billingId}/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(`Error fetching billing detail: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching billing detail:", error.message);
    return null;
  }
}

/**
 * Checks if current medical center user is insolvent
 * @author gabrielm
 */
export async function getIsInsolvent() {
  try {
    const response = await fetch(`${BASE_URL}insurance/is-insolvent/`, {
      headers: {
        ...getAuthHeader(),
      },
    });
    if (!response.ok) {
      throw new Error(`Error fetching is-insolvent: ${response.statusText}`);
    }
    /** @type {{is_insolvent: boolean}} */
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching is-insolvent: ", error.message);
    return { is_insolvent: false };
  }
}

/**
 * Gets billing statistics
 * @author gabrielm
 * @param {number?} clientId - The ID of the client to get statistics for.
 * @param {string?} month - The month to get statistics for, in 'YYYY-MM' format.
 */
export async function getBillingStats(clientId, month) {
  try {
    const params = new URLSearchParams();
    if (month) {
      params.append("month", month);
    }
    if (clientId) {
      params.append("company_client", clientId);
    }
    const response = await fetch(
      `${BASE_URL}insurance/billing-stats/?${params.toString()}`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Error fetching billing stats: ${response.statusText}`);
    }
    /** @type {{ total_pending: number, total_paid: number }} */
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching billing stats:", error.message);
    return { total_pending: 0, total_paid: 0 };
  }
}

/**
 * Returns the history of a specific billing.
 * @author gabrielm
 * @param {number} billingId
 */
export async function getBillingHistory(billingId) {
  try {
    const response = await fetch(
      `${BASE_URL}insurance/billing/${billingId}/history/`,
      {
        headers: {
          ...getAuthHeader(),
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Error fetching billing history: ${response.statusText}`);
    }
    /** @type {{history: Array<{status: string, author: string?, timestamp: string, observation: string}>}} */
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching billing history:", error.message);
    return [];
  }
}
